import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProductCatalog = ({ products, categories, onAddToCart, onBarcodeSearch, onALUSearch, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [scanMode, setScanMode] = useState('barcode');
  const [scanInput, setScanInput] = useState('');

  const handleScan = async (e) => {
    e?.preventDefault();
    if (!scanInput?.trim()) return;

    const result = scanMode === 'barcode'
      ? await onBarcodeSearch(scanInput)
      : await onALUSearch(scanInput);

    if (result?.success) {
      setScanInput('');
    } else {
      alert(`Product not found with ${scanMode}: ${scanInput}`);
    }
  };

  const filteredProducts = products?.filter(product => {
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.barcode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.alu?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product?.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-card rounded-xl shadow-md border border-border">
      <div className="p-4 border-b border-border">
        <h2 className="font-semibold text-foreground mb-4">Product Catalog</h2>
        
        {/* Barcode/ALU Scanner */}
        <form onSubmit={handleScan} className="mb-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={`Scan or enter ${scanMode}...`}
                value={scanInput}
                onChange={(e) => setScanInput(e?.target?.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => setScanMode(scanMode === 'barcode' ? 'alu' : 'barcode')}
              className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm font-medium transition-colors"
            >
              {scanMode === 'barcode' ? 'Barcode' : 'ALU'}
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors"
            >
              <Icon name="Scan" size={18} />
            </button>
          </div>
        </form>

        {/* Search and Filter */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <Select
            value={selectedCategory}
            onChange={setSelectedCategory}
            options={[
              { value: 'all', label: 'All Categories' },
              ...categories?.map(cat => ({ value: cat?.id, label: cat?.name }))
            ]}
            className="w-48"
          />
        </div>
      </div>

      <div className="p-4 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6]?.map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-32 bg-muted rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filteredProducts?.map(product => {
              const isLowStock = product?.stock_quantity <= product?.low_stock_threshold;
              const isOutOfStock = product?.stock_quantity === 0;
              
              return (
                <button
                  key={product?.id}
                  onClick={() => !isOutOfStock && onAddToCart(product)}
                  disabled={isOutOfStock}
                  className={`p-4 rounded-lg transition-colors text-left focus-ring ${
                    isOutOfStock 
                      ? 'bg-muted/20 opacity-60 cursor-not-allowed' :'bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                        {product?.name}
                      </p>
                      <p className="text-xs text-muted-foreground mb-1">
                        {product?.category?.name || 'Uncategorized'}
                      </p>
                    </div>
                    {!isOutOfStock && (
                      <Icon name="Plus" size={16} color="var(--color-primary)" />
                    )}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-lg font-bold text-primary data-text">
                      ${product?.price?.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-1">
                      {isOutOfStock ? (
                        <span className="text-xs font-medium text-error px-2 py-1 bg-error/10 rounded">
                          Out of Stock
                        </span>
                      ) : isLowStock ? (
                        <span className="text-xs font-medium text-warning px-2 py-1 bg-warning/10 rounded">
                          Low: {product?.stock_quantity}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Stock: {product?.stock_quantity}
                        </span>
                      )}
                    </div>
                  </div>
                  {product?.barcode && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Barcode: {product?.barcode}
                    </p>
                  )}
                  {product?.alu && (
                    <p className="text-xs text-muted-foreground">
                      ALU: {product?.alu}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCatalog;